import { useMemo, useState } from 'react';
import {
  Anchor,
  Button,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Image,
  PasswordInput,
  PinInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconCopy, IconDownload, IconShieldLock } from '@tabler/icons-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { saveBlob } from '@web/lib/api';
import { ProblemAlert } from '@web/ui/ProblemAlert';
import { useAuth } from '../AuthProvider';
import { PASSWORD_MIN, newPasswordSchema, type NewPasswordValues } from '../passwordSchema';
import type { LoginMfaEnrollRequired, LoginOk, LoginResponse } from '../types';
import { AuthLayout } from './AuthLayout';

type Step =
  | { kind: 'credentials' }
  | { kind: 'mfa'; challengeToken: string }
  | { kind: 'enroll'; data: LoginMfaEnrollRequired }
  | { kind: 'recovery'; ok: LoginOk; codes: string[] }
  | { kind: 'change'; changeToken: string };

/**
 * Sign-in page. Handles every status of `POST /auth/login`:
 * OK, MFA_REQUIRED (TOTP), MFA_ENROLL_REQUIRED (QR + code + recovery codes), PASSWORD_CHANGE_REQUIRED.
 */
export default function LoginPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const handle = async (fn: () => Promise<LoginResponse>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      switch (res.status) {
        case 'OK':
          if (res.recoveryCodes?.length) setStep({ kind: 'recovery', ok: res, codes: res.recoveryCodes });
          else await auth.completeLogin(res);
          break;
        case 'MFA_REQUIRED':
          setStep({ kind: 'mfa', challengeToken: res.challengeToken });
          break;
        case 'MFA_ENROLL_REQUIRED':
          setStep({ kind: 'enroll', data: res });
          break;
        case 'PASSWORD_CHANGE_REQUIRED':
          setStep({ kind: 'change', changeToken: res.changeToken });
          break;
        default:
          setError(new Error(t('common:auth.unexpectedStatus')));
      }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setError(null);
    setStep({ kind: 'credentials' });
  };


  return (
    <AuthLayout
      title={
        step.kind === 'credentials'
          ? t('common:auth.signInTitle')
          : step.kind === 'mfa'
            ? t('common:auth.mfaTitle')
            : step.kind === 'enroll'
              ? t('common:auth.enrollTitle')
              : step.kind === 'recovery'
                ? t('common:auth.recoveryTitle')
                : t('common:auth.changeRequiredTitle')
      }
      subtitle={step.kind === 'credentials' ? t('common:auth.signInSubtitle') : undefined}
    >
      {error ? <ProblemAlert error={error} onClose={() => setError(null)} /> : null}
      {step.kind === 'credentials' && <CredentialsStep busy={busy} onSubmit={(l, p) => handle(() => auth.login(l, p))} />}
      {step.kind === 'mfa' && (
        <MfaStep busy={busy} onSubmit={(code) => handle(() => auth.verifyMfa(step.challengeToken, code))} onBack={restart} />
      )}
      {step.kind === 'enroll' && (
        <EnrollStep data={step.data} busy={busy} onSubmit={(code) => handle(() => auth.enrollMfa(step.data.enrollToken, code))} onBack={restart} />
      )}
      {step.kind === 'recovery' && <RecoveryCodesStep codes={step.codes} onDone={() => handle(async () => ({ ...step.ok, recoveryCodes: [] }))} />}
      {step.kind === 'change' && (
        <ChangeRequiredStep busy={busy} onSubmit={(pw) => handle(() => auth.changeRequiredPassword(step.changeToken, pw))} onBack={restart} />
      )}
    </AuthLayout>
  );
}

function CredentialsStep({ busy, onSubmit }: { busy: boolean; onSubmit: (login: string, password: string) => void }) {
  const { t } = useTranslation();
  const schema = useMemo(
    () =>
      z.object({
        login: z.string().trim().min(1, t('common:validation.required')),
        password: z.string().min(1, t('common:validation.required')),
      }),
    [t],
  );
  const form = useForm<{ login: string; password: string }>({ resolver: zodResolver(schema), defaultValues: { login: '', password: '' } });
  return (
    <form onSubmit={form.handleSubmit((v) => onSubmit(v.login, v.password))} noValidate>
      <Stack gap="sm">
        <TextInput
          label={t('common:auth.login')}
          description={t('common:auth.loginHint')}
          autoComplete="username"
          dir="ltr"
          autoFocus
          required
          error={form.formState.errors.login?.message}
          {...form.register('login')}
        />
        <PasswordInput
          label={t('common:auth.password')}
          autoComplete="current-password"
          required
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <Group justify="flex-end">
          <Anchor component={Link} to="/forgot-password" size="sm">
            {t('common:auth.forgotPassword')}
          </Anchor>
        </Group>
        <Button type="submit" loading={busy} fullWidth>
          {t('common:auth.signIn')}
        </Button>
      </Stack>
    </form>
  );
}

function CodeEntry({ busy, onSubmit, submitLabel }: { busy: boolean; onSubmit: (code: string) => void; submitLabel: string }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recovery, setRecovery] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const v = useRecovery ? recovery.trim() : code;
        if (v) onSubmit(v);
      }}
    >
      <Stack gap="sm">
        {useRecovery ? (
          <TextInput label={t('common:auth.recoveryCode')} value={recovery} onChange={(e) => setRecovery(e.currentTarget.value)} dir="ltr" autoFocus autoComplete="one-time-code" />
        ) : (
          <Stack gap={4} align="center">
            <Text size="sm" id="otp-label">
              {t('common:auth.enterCode')}
            </Text>
            <Group justify="center" dir="ltr">
              <PinInput
                length={6}
                type="number"
                oneTimeCode
                autoFocus
                size="lg"
                value={code}
                onChange={setCode}
                onComplete={(v) => onSubmit(v)}
                aria-labelledby="otp-label"
                getInputProps={(index) => ({ 'aria-label': t('common:auth.digit', { n: index + 1 }) })}
              />
            </Group>
          </Stack>
        )}
        <Button type="submit" loading={busy} fullWidth disabled={useRecovery ? !recovery.trim() : code.length < 6}>
          {submitLabel}
        </Button>
        <Anchor component="button" type="button" size="sm" onClick={() => setUseRecovery((v) => !v)}>
          {useRecovery ? t('common:auth.useAuthenticator') : t('common:auth.useRecoveryCode')}
        </Anchor>
      </Stack>
    </form>
  );
}

function MfaStep({ busy, onSubmit, onBack }: { busy: boolean; onSubmit: (code: string) => void; onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        {t('common:auth.mfaHint')}
      </Text>
      <CodeEntry busy={busy} onSubmit={onSubmit} submitLabel={t('common:auth.verify')} />
      <Anchor component="button" type="button" size="sm" onClick={onBack}>
        {t('common:auth.backToSignIn')}
      </Anchor>
    </Stack>
  );
}

function EnrollStep({
  data,
  busy,
  onSubmit,
  onBack,
}: {
  data: LoginMfaEnrollRequired;
  busy: boolean;
  onSubmit: (code: string) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  return (
    <Stack gap="sm">
      <Text size="sm">{t('common:auth.enrollHint')}</Text>
      <Group justify="center">
        <Image src={data.qrDataUrl} alt={t('common:auth.qrAlt')} w={200} h={200} fit="contain" bg="white" p={8} radius="md" />
      </Group>
      <Text size="sm" c="dimmed">
        {t('common:auth.enrollManual')}
      </Text>
      <Group gap="xs" wrap="nowrap" justify="center">
        <Code fz="sm" dir="ltr" style={{ wordBreak: 'break-all' }}>
          {data.secret}
        </Code>
        <CopyButton value={data.secret}>
          {({ copied, copy }) => (
            <Button size="compact-sm" variant="light" leftSection={<IconCopy size={14} aria-hidden />} onClick={copy}>
              {copied ? t('common:actions.copied') : t('common:actions.copy')}
            </Button>
          )}
        </CopyButton>
      </Group>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 6) onSubmit(code);
        }}
      >
        <Stack gap="sm" align="stretch">
          <Text size="sm" ta="center" id="enroll-code-label">
            {t('common:auth.enterCode')}
          </Text>
          <Group justify="center" dir="ltr">
            <PinInput length={6} type="number" oneTimeCode size="lg" value={code} onChange={setCode} aria-labelledby="enroll-code-label" />
          </Group>
          <Button type="submit" loading={busy} disabled={code.length < 6} leftSection={<IconShieldLock size={18} aria-hidden />}>
            {t('common:auth.enableMfa')}
          </Button>
        </Stack>
      </form>
      <Anchor component="button" type="button" size="sm" onClick={onBack}>
        {t('common:auth.backToSignIn')}
      </Anchor>
    </Stack>
  );
}

/** Shows MFA recovery codes once; the user must confirm they stored them. */
export function RecoveryCodesStep({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation();
  const [ack, setAck] = useState(false);
  const text = codes.join('\n');
  return (
    <Stack gap="sm">
      <Text size="sm">{t('common:auth.recoveryHint')}</Text>
      <SimpleGrid cols={2} spacing="xs" dir="ltr">
        {codes.map((c) => (
          <Code key={c} fz="md" ta="center" py={6}>
            {c}
          </Code>
        ))}
      </SimpleGrid>
      <Group grow>
        <CopyButton value={text}>
          {({ copied, copy }) => (
            <Button variant="light" leftSection={<IconCopy size={16} aria-hidden />} onClick={copy}>
              {copied ? t('common:actions.copied') : t('common:actions.copy')}
            </Button>
          )}
        </CopyButton>
        <Button
          variant="light"
          leftSection={<IconDownload size={16} aria-hidden />}
          onClick={() => saveBlob(new Blob([text], { type: 'text/plain' }), 'fatoorati-recovery-codes.txt')}
        >
          {t('common:actions.download')}
        </Button>
      </Group>
      <Checkbox checked={ack} onChange={(e) => setAck(e.currentTarget.checked)} label={t('common:auth.recoveryAck')} />
      <Button disabled={!ack} onClick={onDone}>
        {t('common:actions.continue')}
      </Button>
    </Stack>
  );
}

function ChangeRequiredStep({ busy, onSubmit, onBack }: { busy: boolean; onSubmit: (pw: string) => void; onBack: () => void }) {
  const { t } = useTranslation();
  const schema = useMemo(() => newPasswordSchema(), [t]);
  const form = useForm<NewPasswordValues>({ resolver: zodResolver(schema), defaultValues: { password: '', confirm: '' } });
  return (
    <form onSubmit={form.handleSubmit((v) => onSubmit(v.password))} noValidate>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t('common:auth.changeRequiredHint')}
        </Text>
        <NewPasswordFields form={form} />
        <Button type="submit" loading={busy}>
          {t('common:auth.changePassword')}
        </Button>
        <Anchor component="button" type="button" size="sm" onClick={onBack}>
          {t('common:auth.backToSignIn')}
        </Anchor>
      </Stack>
    </form>
  );
}

/** New password + confirmation inputs bound to a `{ password, confirm }` form. */
export function NewPasswordFields({ form }: { form: UseFormReturn<NewPasswordValues> }) {
  const { t } = useTranslation();
  return (
    <>
      <PasswordInput
        label={t('common:auth.newPassword')}
        description={t('common:auth.passwordHint', { min: PASSWORD_MIN })}
        autoComplete="new-password"
        required
        error={form.formState.errors.password?.message}
        {...form.register('password')}
      />
      <PasswordInput
        label={t('common:auth.confirmPassword')}
        autoComplete="new-password"
        required
        error={form.formState.errors.confirm?.message}
        {...form.register('confirm')}
      />
    </>
  );
}
